import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { FileText, Loader2, Paperclip, Send, X } from "lucide-react";
import { errMsg } from "@/lib/api";

const TYPES = ["image/jpeg", "image/png", "image/webp", "image/heic", "application/pdf"];

export const downloadBlob = async (client, url, fallbackName) => {
  try {
    const r = await client.get(url, { responseType: "blob" });
    const name = (r.headers["content-disposition"] || "").match(/filename="([^"]+)"/)?.[1] || fallbackName;
    const href = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = href;
    a.download = name;
    a.click();
    URL.revokeObjectURL(href);
  } catch (e) {
    toast.error("Could not download the PDF");
  }
};

const Bubble = ({ m, mine, fileUrl }) => (
  <div className={`flex ${mine ? "justify-end" : "justify-start"}`}>
    <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm ${mine ? "bg-[#020165] text-white rounded-br-sm" : "bg-[#F4F4F4] text-[#0D141A] rounded-bl-sm"}`} data-testid={`thread-message-${m.id}`}>
      <p className={`text-[11px] font-semibold ${mine ? "text-white/70" : "text-[#56585E]"}`}>{m.sender_name} · {new Date(m.created_at).toLocaleString("en-ZW")}</p>
      <p className="mt-1 whitespace-pre-line break-words">{m.body}</p>
      {m.attachments?.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {m.attachments.map((a) => (
            <a key={a.id} href={fileUrl(a.id)} target="_blank" rel="noreferrer" data-testid={`thread-attachment-${a.id}`}
              className={`inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs underline ${mine ? "bg-white/10" : "bg-white"}`}>
              {a.content_type?.startsWith("image/") ? <img src={fileUrl(a.id)} alt="" className="h-12 w-12 rounded object-cover" /> : <FileText className="h-3.5 w-3.5" />}{a.filename || "attachment"}
            </a>
          ))}
        </div>
      )}
    </div>
  </div>
);

export const InsuranceThread = ({ client, basePath, uploadPath, fileUrl, me, title = "Messages" }) => {
  const [msgs, setMsgs] = useState([]);
  const [body, setBody] = useState("");
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(0);
  const [busy, setBusy] = useState(false);
  const end = useRef(null);
  const load = useCallback(() => { client.get(`${basePath}/messages`).then((r) => setMsgs(r.data.messages)).catch(() => {}); }, [client, basePath]);
  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [load]);
  useEffect(() => { end.current?.scrollIntoView({ block: "nearest" }); }, [msgs]);

  const attach = async (list) => {
    for (const file of Array.from(list)) {
      if (!TYPES.includes(file.type)) { toast.error(`${file.name}: only photos or PDF`); continue; }
      if (file.size > 10 * 1024 * 1024) { toast.error(`${file.name}: larger than 10 MB`); continue; }
      const fd = new FormData();
      fd.append("file", file);
      try {
        const r = await client.post(uploadPath, fd, { onUploadProgress: (e) => setUploading(Math.max(1, Math.round((e.loaded / (e.total || 1)) * 100))) });
        setFiles((f) => [...f, r.data]);
      } catch (e) { toast.error(errMsg(e, "Upload failed")); }
      setUploading(0);
    }
  };
  const send = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setBusy(true);
    try {
      await client.post(`${basePath}/messages`, { body, attachment_ids: files.map((f) => f.id) });
      setBody(""); setFiles([]); load();
    } catch (err) { toast.error(errMsg(err, "Message not sent")); } finally { setBusy(false); }
  };

  return (
    <section className="rounded-2xl bg-white p-5" data-testid="insurance-thread">
      <h2 className="font-display font-bold text-lg mb-3">{title}</h2>
      <div className="max-h-96 overflow-y-auto space-y-3 pr-1" data-testid="insurance-thread-list">
        {msgs.map((m) => <Bubble key={m.id} m={m} mine={m.sender_type === me} fileUrl={fileUrl} />)}
        {!msgs.length && <p className="text-sm text-[#56585E]" data-testid="insurance-thread-empty">No messages yet. Start the conversation below.</p>}
        <div ref={end} />
      </div>
      <form onSubmit={send} className="mt-4 space-y-2">
        {(files.length > 0 || uploading > 0) && (
          <div className="flex flex-wrap gap-2 text-xs">
            {files.map((f) => (
              <span key={f.id} className="inline-flex items-center gap-1 rounded-full bg-[#F4F4F4] px-2.5 py-1" data-testid="insurance-thread-pending-attachment">{f.filename}
                <button type="button" onClick={() => setFiles(files.filter((x) => x.id !== f.id))} aria-label="Remove attachment"><X className="h-3 w-3" /></button></span>
            ))}
            {uploading > 0 && <span className="inline-flex items-center gap-1 text-[#56585E]"><Loader2 className="h-3 w-3 animate-spin" />Uploading {uploading}%</span>}
          </div>
        )}
        <div className="flex items-end gap-2">
          <label className="cursor-pointer rounded-full border p-2.5 text-[#56585E] hover:border-[#020165] hover:text-[#020165]" aria-label="Attach file">
            <Paperclip className="h-4 w-4" />
            <input type="file" multiple accept={TYPES.join(",")} className="hidden" onChange={(e) => { attach(e.target.files); e.target.value = ""; }} data-testid="insurance-thread-file-input" />
          </label>
          <textarea rows={2} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Write a message…" className="flex-1 rounded-xl border p-3 text-sm" data-testid="insurance-thread-input" />
          <button disabled={busy || uploading > 0 || !body.trim()} className="rounded-full bg-[#F86900] p-3 text-[#0D141A] disabled:opacity-50" data-testid="insurance-thread-send" aria-label="Send message"><Send className="h-4 w-4" /></button>
        </div>
      </form>
    </section>
  );
};
